import Discount from "@/data/interfaces/Discount";
import MedicalReportPrompt from "@/data/interfaces/MedicalReportPrompt";
import MedicalReportPromptType from "@/data/interfaces/MedicalReportPromptType";
import {
  InsightType,
  InsightPrompt,
  InsightTypeEnum,
} from "@/data/interfaces/InsightType";
import SupplementPrompt from "@/data/interfaces/SupplementPrompt";
import api from "./api";

const discount = {
  updateDiscount: async (discount: Discount) => {
    await api.put(`/discount-codes/${discount.id}`, {
      is_active: discount.is_active,
    });
    return discount;
  },
  addNew: async (
    code: string,
    value: number,
    type: "percent" | "fixed",
    name: string,
    is_one_time?: boolean,
  ) => {
    const resp = await api.post("/discount-codes", {
      code,
      name,
      value,
      type,
      is_active: true,
      is_one_time: is_one_time || false,
    });

    return resp.data;
  },
  getAll: async () => {
    const resp = await api.get("/discount-codes");
    return resp.data.data as Discount[];
  },
};

const medicalReportPromptType = {
  getAll: async () => {
    const resp = await api.get("/medical-report-pdf-types");
    return resp.data.data as MedicalReportPromptType[];
  },
  create: async (name: string) => {
    const resp = await api.post("/medical-report-pdf-types", {
      name,
      is_active: true,
      is_default: false,
    });
    return resp.data as MedicalReportPromptType;
  },
  update: async (promptType: MedicalReportPromptType) => {
    const resp = await api.put(`/medical-report-pdf-types/${promptType.id}`, {
      name: promptType.name,
      is_active: promptType.is_active,
      is_default: promptType.is_default,
    });
    return resp.data as MedicalReportPromptType;
  },
  delete: async (id: number) => {
    await api.delete(`/medical-report-pdf-types/${id}`);
  },
};

const medicalReportPrompt = {
  getByType: async (promptTypeId: number) => {
    const resp = await api.get("/medical-report-pdf-prompts", {
      params: { type_id: promptTypeId },
    });
    return resp.data.data as MedicalReportPrompt[];
  },
  create: async (promptTypeId: number, name: string, content: string) => {
    const resp = await api.post("/medical-report-pdf-prompts", {
      medical_report_type_id: promptTypeId,
      name,
      content,
      is_active: true,
    });
    return resp.data as MedicalReportPrompt;
  },
  update: async (prompt: MedicalReportPrompt) => {
    const resp = await api.put(`/medical-report-pdf-prompts/${prompt.id}`, {
      name: prompt.name,
      content: prompt.content,
      is_active: prompt.is_active,
    });
    return resp.data as MedicalReportPrompt;
  },
  delete: async (id: number) => {
    await api.delete(`/medical-report-pdf-prompts/${id}`);
  },
};

const healthInsightType = {
  getAll: async (params?: {
    per_page?: number;
    q?: string;
    is_active?: boolean;
  }) => {
    const resp = await api.get("/insight-types", {
      params: {
        type: InsightTypeEnum.HEALTH,
        ...params,
      },
    });
    return resp.data.data as InsightType[];
  },
  create: async (name: string) => {
    const resp = await api.post("/insight-types", {
      name,
      type: InsightTypeEnum.HEALTH,
      is_active: true,
    });
    return resp.data as InsightType;
  },
  update: async (insightType: InsightType) => {
    const resp = await api.put(`/insight-types/${insightType.id}`, {
      name: insightType.name,
      type: InsightTypeEnum.HEALTH,
      is_active: insightType.is_active,
    });
    return resp.data as InsightType;
  },
  delete: async (id: number) => {
    await api.delete(`/insight-types/${id}`);
  },
};

const healthInsightPrompt = {
  getByType: async (insightTypeId: number) => {
    const resp = await api.get("/insight-prompts", {
      params: { insight_type_id: insightTypeId },
    });
    return resp.data.data as InsightPrompt[];
  },
  create: async (insightTypeId: number, name: string, content: string) => {
    const resp = await api.post("/insight-prompts", {
      insight_type_id: insightTypeId,
      name,
      content,
      is_active: true,
    });
    return resp.data as InsightPrompt;
  },
  update: async (prompt: InsightPrompt) => {
    const resp = await api.put(`/insight-prompts/${prompt.id}`, {
      name: prompt.name,
      content: prompt.content,
      is_active: prompt.is_active,
    });
    return resp.data as InsightPrompt;
  },
  delete: async (id: number) => {
    await api.delete(`/insight-prompts/${id}`);
  },
};

const actionPlanType = {
  getAll: async (params?: {
    per_page?: number;
    q?: string;
    is_active?: boolean;
  }) => {
    const resp = await api.get("/insight-types", {
      params: {
        type: InsightTypeEnum.ACTION,
        ...params,
      },
    });
    return resp.data.data as InsightType[];
  },
  create: async (data: {
    name: string;
    description?: string;
    image?: File;
  }) => {
    const formData = new FormData();
    formData.append("name", data.name);
    formData.append("type", String(InsightTypeEnum.ACTION));
    formData.append("is_active", "1");

    if (data.description != null && data.description !== "") {
      formData.append("description", data.description);
    }

    if (data.image instanceof File) {
      formData.append("image", data.image);
    }

    const resp = await api.post("/insight-types", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return resp.data as InsightType;
  },
  update: async (
    insightType: InsightType & { image?: string | File | null },
  ): Promise<InsightType> => {
    const formData = new FormData();
    formData.append("name", insightType.name);
    formData.append("type", String(InsightTypeEnum.ACTION));
    formData.append("is_active", insightType.is_active ? "1" : "0");
    formData.append("description", insightType.description ?? "");
    formData.append("_method", "PUT");

    if (
      insightType.image !== null &&
      insightType.image !== undefined &&
      typeof insightType.image !== "string"
    ) {
      formData.append("image", insightType.image);
    }

    const resp = await api.post(`/insight-types/${insightType.id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return resp.data as InsightType;
  },
  delete: async (id: number) => {
    await api.delete(`/insight-types/${id}`);
  },
};

const actionPlanPrompt = {
  getByType: async (insightTypeId: number) => {
    const resp = await api.get("/insight-prompts", {
      params: { insight_type_id: insightTypeId },
    });
    return resp.data.data as InsightPrompt[];
  },
  create: async (insightTypeId: number, name: string, content: string) => {
    const resp = await api.post("/insight-prompts", {
      insight_type_id: insightTypeId,
      name,
      content,
      is_active: true,
    });
    return resp.data as InsightPrompt;
  },
  update: async (prompt: InsightPrompt) => {
    const resp = await api.put(`/insight-prompts/${prompt.id}`, {
      name: prompt.name,
      content: prompt.content,
      is_active: prompt.is_active,
    });
    return resp.data as InsightPrompt;
  },
  delete: async (id: number) => {
    await api.delete(`/insight-prompts/${id}`);
  },
};

const supplementPrompt = {
  getAll: async () => {
    const resp = await api.get("/supplement-prompts");
    return resp.data.data as SupplementPrompt[];
  },
  create: async (content: string) => {
    const resp = await api.post("/supplement-prompts", {
      content,
      is_active: true,
    });
    return resp.data as SupplementPrompt;
  },
  update: async (prompt: SupplementPrompt) => {
    const resp = await api.put(`/supplement-prompts/${prompt.id}`, {
      content: prompt.content,
      is_active: prompt.is_active,
    });
    return resp.data as SupplementPrompt;
  },
  delete: async (id: number) => {
    await api.delete(`/supplement-prompts/${id}`);
  },
};

export default {
  discount,
  medicalReportPromptType,
  medicalReportPrompt,
  healthInsightType,
  healthInsightPrompt,
  actionPlanType,
  actionPlanPrompt,
  supplementPrompt,
};
